import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Save, Trash2, ShieldCheck } from "lucide-react";

/**
 * Kassierer-PIN-Verwaltung. Wird in Settings.jsx eingebettet.
 * Erlaubt setzen/löschen der eigenen POS-Schnellentsperrungs-PIN (4-8 Ziffern).
 */
export default function CashierPinCard() {
  const [hasPin, setHasPin] = useState(false);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users/me/pin-status");
      setHasPin(!!data.has_pin);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!pin || pin !== pin2) return toast.error("PINs stimmen nicht überein");
    if (!/^\d{4,8}$/.test(pin)) return toast.error("PIN muss 4-8 Ziffern haben");
    setBusy(true);
    try {
      await api.post("/users/me/pin", { pin });
      toast.success("Kassierer-PIN aktualisiert");
      setPin(""); setPin2(""); setHasPin(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("PIN wirklich löschen?")) return;
    setBusy(true);
    try {
      await api.delete("/users/me/pin");
      toast.success("PIN entfernt");
      setHasPin(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-lux p-5" data-testid="cashier-pin-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={16} className="text-[#D4AF37]" />
        <h3 className="font-heading text-xl">Kassierer-PIN</h3>
        {hasPin && (
          <span className="ml-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#8FBC8F]" data-testid="pin-status-set">
            <ShieldCheck size={11} /> aktiv
          </span>
        )}
      </div>
      <p className="text-[11px] text-[#A8A398] mb-4">
        Kurze Ziffern-PIN für Schnellentsperrung am POS (nach Sperrbildschirm oder Kassierer-Wechsel). Nur du kennst deine PIN.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Neue PIN (4-8 Ziffern)</label>
          <input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8}
            value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            data-testid="pin-input"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-lg tracking-widest focus:border-[#D4AF37] outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Wiederholen</label>
          <input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8}
            value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
            data-testid="pin-input-2"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-lg tracking-widest focus:border-[#D4AF37] outline-none" />
        </div>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <button onClick={save} disabled={busy || !pin} data-testid="pin-save"
          className="bg-[#D4AF37] hover:bg-[#EEDC82] text-black px-4 py-2 rounded text-[11px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40">
          <Save size={13} /> {hasPin ? "PIN aktualisieren" : "PIN setzen"}
        </button>
        {hasPin && (
          <button onClick={remove} disabled={busy} data-testid="pin-delete"
            className="border border-[#7A1921]/60 text-[#EE9CA1] px-4 py-2 rounded text-[11px] uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#7A1921]/20">
            <Trash2 size={13} /> PIN entfernen
          </button>
        )}
      </div>
    </div>
  );
}
